<template>
  <div class="col-sm-12">
    <div class="section-panel room-tables">
      <div class="section-header" :class="options.group_title || ''">
        <div class="section-title">{{ tableGroupName }}</div>
        <div class="section-meta">
          <div class="counts" v-if="room.SHOW_NUMBERS">
            <!-- <span class="count"><i class="fa fa-fw fa-star" aria-hidden="true"></i>Nominee</span> -->
            <span class="count attending">Attending: {{ totalActiveUsers }}</span>
            <span class="count tables">
              {{ groupTerminology }}:
              {{ tables.length }}
            </span>
          </div>
        </div>
      </div>
      <div class="section-body isotope-table-grid" :class="options.table_class">
        <div class="table-tools" v-if="searchable || paginated">
          <div v-if="searchable" class="table-search">
            <input type="text" class="form-control" placeholder="Search..." v-model="tableSearchQuery" />
            <a href="javascript:void(0);" @click="tableSearchQuery = ''" class="clear-search"
              ><i class="fa fa-times" aria-hidden="true"></i
            ></a>
          </div>
          <div v-if="nomineesTablesCount" class="nominee-select">
            <label class="styled-cb">
              <span class="filter-name"
                >Nominees ({{ nomineesTablesCount }})
                <span class="nominee-star"><i class="fa fa-star" aria-hidden="true"></i></span
              ></span>
              <input id="users-mw-my-network" type="checkbox" v-model="filterNominees" />
              <span class="checkmark"></span>
            </label>
          </div>
          <div class="table-pages" v-if="paginated && filteredTables.length > pageSize">
            <pagination
              :page-size="pageSize"
              :current-page="currentPage"
              :total="filteredTables.length"
              @pageChanged="changePage"
            ></pagination>
          </div>
        </div>
        <masonry
          :cols="{ default: 10, 2140: 9, 1900: 8, 1680: 7, 1400: 6, 1190: 5, 890: 4, 680: 3, 550: 2 }"
          :gutter="8"
        >
          <div
            class="grid-item org-table-cont"
            v-for="(table, index) in paginatedTables"
            :key="'table-' + table.uuid"
            :id="'TABLE-' + table.TABLE_UUID"
          >
            <digital-event-room-table :table="table" :number="index" :options="options"></digital-event-room-table>
          </div>
        </masonry>
      </div>
    </div>
  </div>
</template>

<script>
import Table from "@/components/DigitalEvent/RoomList/Room/Tables/Table";
import { debounce, searchOverMultipleAttributes } from "@/helpers/helpers";
import { isEmptyObject } from "@/helpers/ObjectHelpers";
import Pagination from "@/modules/core/components/Pagination";
import { mapGetters } from "vuex";

export default {
  name: "TableList",
  data() {
    return {
      tableSearchQueryText: "",
      filterNominees: false,
      currentPage: 1
    };
  },
  components: {
    "digital-event-room-table": Table,
    pagination: Pagination
  },
  props: {
    tableGroupName: {
      type: String,
      default: "Guest tables"
    },
    tables: {
      type: Array,
      default() {
        return [];
      }
    },
    options: {
      type: Object,
      default: function() {
        return {};
      }
    },
    searchable: {
      type: Boolean,
      default: false
    },
    paginated: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters({
      groupTerminology: "room/groupTerminology",
      room: "room/activeRoom"
    }),
    tableSearchQuery: {
      get() {
        return this.tableSearchQueryText || "";
      },
      set: debounce(function(value) {
        this.currentPage = 1;
        this.tableSearchQueryText = value;
      }, 600)
    },
    pageSize() {
      return this.$store.state.event.config.defaultPaginationConfig &&
        this.$store.state.event.config.defaultPaginationConfig.tables
        ? this.$store.state.event.config.defaultPaginationConfig.tables
        : 50;
    },
    _filteredTables() {
      return this.tables.filter(table => !this.filterNominees || table.IS_NOMINEE);
    },
    filteredTables() {
      return this._filteredTables.filter(table => {
        return (
          !this.tableSearchQuery ||
          table.TITLE.toLowerCase().includes(this.tableSearchQuery.toLowerCase()) ||
          this.tableUsers(table.uuid).filter(user =>
            searchOverMultipleAttributes(
              this.tableSearchQuery,
              user.profile && !isEmptyObject(user.profile)
                ? [user.profile.firstName, user.profile.lastName, user.emailDomain]
                : [user.emailDomain, user.firstName, user.lastName]
            )
          ).length
        );
      });
    },
    paginatedTables() {
      if (!this.paginated) {
        return this.filteredTables;
      }
      const startOffset = (this.currentPage - 1) * this.pageSize;
      return this.filteredTables.slice(startOffset, startOffset + this.pageSize);
    },
    nomineesTablesCount() {
      return this.tables.filter(table => table.IS_NOMINEE).length;
    },
    /**
     * Get total active users count for table list
     * @return {number}
     */
    totalActiveUsers() {
      const tableUUIDs = this.tables.map(table => table.uuid);
      return this.$store.getters["room/roomState"].filter(user => {
        return user.isOnline && tableUUIDs.includes(user.tableUUID);
      }).length;
    },
    /**
     * Get total inactive users count for table list (e.g. for all Guest tables)
     * @return {number}
     */
    totalInactiveUsers() {
      let count = 0;
      this.tables.forEach(t => {
        count += this.inactiveUsers(t.uuid);
      });
      return count;
    }
  },
  methods: {
    /**
     * Get table users
     * @param tableUUID {String}
     * @return {function(*=): unknown[]}
     */
    tableUsers(tableUUID) {
      return this.$store.getters["tables/tableUsers"](tableUUID);
    },

    /**
     * Get active users count for current table
     * @param tableUUID {String}
     * @return {Integer}
     */
    activeUsers(tableUUID) {
      return this.tableUsers(tableUUID).filter(u => u.isOnline).length;
    },

    /**
     * Get inactive users count for current table
     * @param tableUUID {String}
     * @return {Integer}
     */
    inactiveUsers(tableUUID) {
      return this.tableUsers(tableUUID).filter(u => !u.isOnline).length;
    },
    /**
     * Change current page
     * @param pageNumber
     */
    changePage(pageNumber) {
      this.currentPage = pageNumber;
    }
  },
  watch: {
    filterNominees() {
      this.currentPage = 1;
    }
  }
};
</script>
